import React, { useMemo } from 'react'
import {
    Box,
    Chip,
    CircularProgress,
    Container,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    makeStyles,
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { Pagination } from '@material-ui/lab'
import NumberFormat from 'react-number-format'

import { useUser } from '../../hooks'
import { useApplicationFeePayments } from '../../hooks/Outreach/useApplicationFeePayments'
import { useLeasingSeasonsList } from '../../containers/LeasingSeason/hooks/useLeasingSeasonQueries'
import { toMMDDYYYY } from '../../helpers'
import {
    ApplicationFeePaymentListItem,
    ApplicationFeePaymentStatus,
} from '../../models/Outreach/types'

const useStyles = makeStyles((theme) => ({
    pageContainer: {
        padding: theme.spacing(4),
        maxWidth: 1400,
        margin: '0 auto',
    },
    header: {
        marginBottom: theme.spacing(3),
    },
    title: {
        fontWeight: 700,
        marginBottom: theme.spacing(0.5),
    },
    subtitle: {
        color: theme.palette.text.secondary,
    },
    filters: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        alignItems: 'center',
    },
    filterControl: {
        minWidth: 180,
    },
    summary: {
        marginBottom: theme.spacing(2),
        color: theme.palette.text.secondary,
    },
    tablePaper: {
        marginBottom: theme.spacing(2),
    },
    pagination: {
        display: 'flex',
        justifyContent: 'center',
        paddingTop: theme.spacing(2),
    },
    emptyState: {
        padding: theme.spacing(6),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    statusChip: {
        textTransform: 'capitalize',
    },
}))

const STATUS_OPTIONS: Array<{
    value: ApplicationFeePaymentStatus | ''
    label: string
}> = [
    { value: 'succeeded', label: 'Succeeded' },
    { value: '', label: 'All statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'failed', label: 'Failed' },
    { value: 'refunded', label: 'Refunded' },
]

const statusChipColor = (
    status: ApplicationFeePaymentStatus,
): 'default' | 'primary' | 'secondary' => {
    if (status === 'succeeded') return 'primary'
    if (status === 'failed' || status === 'refunded') return 'secondary'
    return 'default'
}

export const FinanceHome: React.FC = () => {
    const classes = useStyles()
    const { workspaceUser } = useUser()
    const apartmentId = workspaceUser?.active_workspace?.id

    const {
        data,
        isLoading,
        filters,
        setFilters,
        pagination,
    } = useApplicationFeePayments()

    const { data: seasons = [] } = useLeasingSeasonsList(apartmentId)

    const payments = data?.results ?? []

    const paidAtStart = useMemo(
        () => (filters.paid_at_start ? new Date(filters.paid_at_start) : null),
        [filters.paid_at_start],
    )
    const paidAtEnd = useMemo(
        () => (filters.paid_at_end ? new Date(filters.paid_at_end) : null),
        [filters.paid_at_end],
    )

    const handleSeasonChange = (value: string) => {
        setFilters({
            ...filters,
            leasing_season_id: value ? Number(value) : undefined,
            page: 1,
        })
    }

    const handleStatusChange = (value: string) => {
        setFilters({
            ...filters,
            status: value as ApplicationFeePaymentStatus | '',
            page: 1,
        })
    }

    const formatPaidDate = (payment: ApplicationFeePaymentListItem) => {
        if (!payment.paid_at) return '—'
        return toMMDDYYYY(new Date(payment.paid_at))
    }

    return (
        <Container maxWidth={false} className={classes.pageContainer}>
            <Box className={classes.header}>
                <Typography variant="h4" className={classes.title}>
                    Finance
                </Typography>
                <Typography variant="body1" className={classes.subtitle}>
                    Application fees collected
                </Typography>
            </Box>

            <Box className={classes.filters}>
                <FormControl
                    variant="outlined"
                    size="small"
                    className={classes.filterControl}
                >
                    <InputLabel id="finance-season-filter">Season</InputLabel>
                    <Select
                        labelId="finance-season-filter"
                        label="Season"
                        value={
                            filters.leasing_season_id != null
                                ? String(filters.leasing_season_id)
                                : ''
                        }
                        onChange={(event) =>
                            handleSeasonChange(event.target.value as string)
                        }
                    >
                        <MenuItem value="">All seasons</MenuItem>
                        {seasons.map((season) => (
                            <MenuItem key={season.id} value={String(season.id)}>
                                {season.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl
                    variant="outlined"
                    size="small"
                    className={classes.filterControl}
                >
                    <InputLabel id="finance-status-filter">Status</InputLabel>
                    <Select
                        labelId="finance-status-filter"
                        label="Status"
                        value={filters.status ?? 'succeeded'}
                        onChange={(event) =>
                            handleStatusChange(event.target.value as string)
                        }
                    >
                        {STATUS_OPTIONS.map((option) => (
                            <MenuItem key={option.label} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <KeyboardDatePicker
                    disableToolbar
                    variant="inline"
                    inputVariant="outlined"
                    format="MM/dd/yyyy"
                    label="Paid from"
                    size="small"
                    value={paidAtStart}
                    onChange={(date) =>
                        setFilters({
                            ...filters,
                            paid_at_start: date
                                ? date.toISOString().slice(0, 10)
                                : undefined,
                            page: 1,
                        })
                    }
                    clearable
                    autoOk
                />

                <KeyboardDatePicker
                    disableToolbar
                    variant="inline"
                    inputVariant="outlined"
                    format="MM/dd/yyyy"
                    label="Paid through"
                    size="small"
                    value={paidAtEnd}
                    minDate={paidAtStart || undefined}
                    onChange={(date) =>
                        setFilters({
                            ...filters,
                            paid_at_end: date
                                ? date.toISOString().slice(0, 10)
                                : undefined,
                            page: 1,
                        })
                    }
                    clearable
                    autoOk
                />
            </Box>

            <Typography variant="body2" className={classes.summary}>
                {pagination.count} payment{pagination.count === 1 ? '' : 's'}
            </Typography>

            {isLoading ? (
                <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress />
                </Box>
            ) : payments.length === 0 ? (
                <Box className={classes.emptyState}>
                    <Typography variant="h6" gutterBottom>
                        No application fees collected yet
                    </Typography>
                    <Typography variant="body2">
                        Set an application fee on your leasing season and send
                        an application from the pipeline to collect fees here.
                    </Typography>
                </Box>
            ) : (
                <>
                    <TableContainer className={classes.tablePaper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Lead</TableCell>
                                    <TableCell>Amount</TableCell>
                                    <TableCell>Paid</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Season</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {payments.map((payment) => (
                                    <TableRow key={payment.id}>
                                        <TableCell>
                                            {payment.lead_name}
                                        </TableCell>
                                        <TableCell>
                                            <NumberFormat
                                                displayType="text"
                                                value={payment.amount}
                                                prefix="$"
                                                thousandSeparator
                                                fixedDecimalScale
                                                decimalScale={2}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {formatPaidDate(payment)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={payment.status}
                                                color={statusChipColor(
                                                    payment.status,
                                                )}
                                                className={classes.statusChip}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {payment.leasing_season_name}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {pagination.totalPages > 1 && (
                        <Box className={classes.pagination}>
                            <Pagination
                                count={pagination.totalPages}
                                page={pagination.currentPage}
                                onChange={(_event, page) =>
                                    setFilters({ ...filters, page })
                                }
                                color="primary"
                            />
                        </Box>
                    )}
                </>
            )}
        </Container>
    )
}
